import { RootState } from "@/redux/store";
import { useGetOwnerAccountDtlByOwnershipId, useGetUserById, useGetUserPreferenceByUserId } from "@/services";
import { useSelector } from "react-redux";
import { ACCOUNT_ID, ACH_ACCOUNT, ACH_ACCOUNT_NAME, ACH_BANK_NAME, ACH_ROUTING, ADDRESS, ADDRESS_1, ADDRESS_2, ADS, ALTERNATE_LOGIN_ID, AUTO_BILLING_ENROLL, AUTO_BILLING_LA, AUTO_BILLING_LA_END_DATE, AUTO_BILLING_LA_START_DATE, AUTO_BILLING_START_DATE, CITY, CORRESPONDENCE_ADDRESS, DISPLAY_EMAIL, DISPLAY_LANDLINE, DISPLAY_MOBILE, DISPLAY_NAME, E_ACCOUNT_EMAIL, E_ACCOUNT_NAME, E_ACCOUNT_NO, E_ACCOUNT_PHONE, E_COM_SIGNUP, E_COM_START_DATE, EMAIL_ID_1, EMAIL_ID_2, FIRST_NAME, LANDLINE_NO, LAST_NAME, MOBILE_NO, OPT_EMERGENCY, OPT_GENERAL, PARTNER_EMAIL, PAYMENT_TYPE, PO_BOX, PREFERENCE_ID, PROP_ADDRESS, SMS_PAST_DUE, STATE, USER_ID, USERNAME, ZELLE_URL, ZIPCODE } from "./consts";
import { useForm } from "react-hook-form";
import { useEffect } from "react";
import html2canvas from 'html2canvas-pro';
import jsPDF from 'jspdf';

const useDownloadFile = () => {
  const {loggedInUserData, stateData: stateList, communityData} = useSelector((state: RootState) => state.currentData);
  
  const form = useForm<any>({
    defaultValues: {
      [USER_ID]: "",
      [ALTERNATE_LOGIN_ID]: "",
      [FIRST_NAME]: "",
      [LAST_NAME]: "",
      [MOBILE_NO]: "",
      [LANDLINE_NO]: "",
      [EMAIL_ID_1]: "",
      [EMAIL_ID_2]: undefined,
      [ADDRESS_1]: "",
      [ADDRESS_2]: undefined,
      [ZIPCODE]: "",
      [STATE]: "",
      [CITY]: "",
      [PO_BOX]: "",
      [USERNAME]: "",

      [PREFERENCE_ID]: "",
      [SMS_PAST_DUE]: false,
      [OPT_GENERAL]: false,
      [OPT_EMERGENCY]: false,
      [DISPLAY_NAME]: false,
      [PROP_ADDRESS]: false,
      [DISPLAY_EMAIL]: false,
      [DISPLAY_MOBILE]: false,
      [DISPLAY_LANDLINE]: false,
      [ADS]: false,
      [PARTNER_EMAIL]: false,

      [ACCOUNT_ID]: "",
      [PAYMENT_TYPE]: "",
      [ZELLE_URL]: undefined,
      [ACH_ROUTING]: undefined,
      [ACH_ACCOUNT]: undefined,
      [ACH_ACCOUNT_NAME]: undefined,
      [ACH_BANK_NAME]: undefined,
      [E_ACCOUNT_NO]: undefined,
      [E_ACCOUNT_NAME]: undefined,
      [E_ACCOUNT_EMAIL]: undefined,
      [E_ACCOUNT_PHONE]: undefined,
      [E_COM_SIGNUP]: undefined,
      [E_COM_START_DATE]: undefined,
      [AUTO_BILLING_ENROLL]: undefined,
      [AUTO_BILLING_START_DATE]: undefined,
      [AUTO_BILLING_LA]: undefined,
      [AUTO_BILLING_LA_START_DATE]: undefined,
      [AUTO_BILLING_LA_END_DATE]: undefined,
    },
  });

  const {
    data: userDetails,
    isLoading: isUserDetailsLoading,
  } = useGetUserById({
    id: loggedInUserData?.userId,
    queryParams: {
      enabled: !!loggedInUserData?.userId,
    },
  });

  //get properties
  const {
    data: userPreference,
    isLoading: isLoadingUserPreference,
  } = useGetUserPreferenceByUserId({
    userId: loggedInUserData?.userId as string,
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!loggedInUserData?.userId,
    },
  });

  // get owner account details
  const {
    data: ownerAccountDtlDetails,
    isLoading: isOwnerAccountDtlDetailsLoading,
  } = useGetOwnerAccountDtlByOwnershipId({
    id: loggedInUserData?.userId as string,
    communityId: communityData?.communityId as string,
    queryParams: {
      enabled: !!(loggedInUserData?.userId && communityData?.communityId),
    },
  });

  useEffect(() => {
    if (!userDetails && !userPreference && !ownerAccountDtlDetails) return;
  
    const currentValues = form.getValues();
  
    form.reset({
      ...currentValues,
  
      // ---------------- USER ----------------
      ...(userDetails && {
        [USER_ID]: userDetails.data?.[USER_ID] || "",
        [ALTERNATE_LOGIN_ID]: userDetails.data?.[ALTERNATE_LOGIN_ID] || "",
        [FIRST_NAME]: userDetails.data?.[FIRST_NAME] || "",
        [LAST_NAME]: userDetails.data?.[LAST_NAME] || "",
        [MOBILE_NO]: userDetails.data?.[MOBILE_NO] || "",
        [LANDLINE_NO]: userDetails.data?.[LANDLINE_NO] || undefined,
        [EMAIL_ID_1]: userDetails.data?.[EMAIL_ID_1] || "",
        [EMAIL_ID_2]: userDetails.data?.[EMAIL_ID_2] || undefined,
        [ADDRESS_1]: userDetails.data?.[ADDRESS]?.[ADDRESS_1] || "",
        [ADDRESS_2]: userDetails.data?.[ADDRESS]?.[ADDRESS_2] || undefined,
        [ZIPCODE]: userDetails.data?.[ADDRESS]?.[ZIPCODE] || "",
        [STATE]:
          stateList?.find(
            (state: { name: string; code: string }) =>
              state.name == userDetails.data?.[ADDRESS]?.[STATE] ||
              state.code == userDetails.data?.[ADDRESS]?.[STATE]
          )?.code || "",
        [CITY]: userDetails.data?.[ADDRESS]?.[CITY] || "",
        [PO_BOX]: userDetails.data?.[ADDRESS]?.[PO_BOX] || "",
  
        [CORRESPONDENCE_ADDRESS]: {
          [ADDRESS_1]:
            userDetails.data?.[CORRESPONDENCE_ADDRESS]?.[ADDRESS_1] || "",
          [ADDRESS_2]:
            userDetails.data?.[CORRESPONDENCE_ADDRESS]?.[ADDRESS_2] || "",
          [ZIPCODE]:
            userDetails.data?.[CORRESPONDENCE_ADDRESS]?.[ZIPCODE] || "",
          [STATE]:
            userDetails.data?.[CORRESPONDENCE_ADDRESS]?.[STATE] || "",
          [CITY]:
            userDetails.data?.[CORRESPONDENCE_ADDRESS]?.[CITY] || "",
          [PO_BOX]:
            userDetails.data?.[CORRESPONDENCE_ADDRESS]?.[PO_BOX] || "",
        },
  
        [USERNAME]: userDetails.data?.[USERNAME] || "",
      }),
  
      // ---------------- PREFERENCE ----------------
      ...(userPreference && {
        [PREFERENCE_ID]:
          userPreference.data?.[PREFERENCE_ID]?.toString() || "",
        [SMS_PAST_DUE]: userPreference.data?.[SMS_PAST_DUE] || false,
        [OPT_GENERAL]: userPreference.data?.[OPT_GENERAL] || false,
        [OPT_EMERGENCY]: userPreference.data?.[OPT_EMERGENCY] || false,
        [DISPLAY_NAME]: userPreference.data?.[DISPLAY_NAME] || false,
        [PROP_ADDRESS]: userPreference.data?.[PROP_ADDRESS] || false,
        [DISPLAY_EMAIL]: userPreference.data?.[DISPLAY_EMAIL] || false,
        [DISPLAY_MOBILE]: userPreference.data?.[DISPLAY_MOBILE] || false,
        [DISPLAY_LANDLINE]: userPreference.data?.[DISPLAY_LANDLINE] || false,
        [ADS]: userPreference.data?.[ADS] || false,
        [PARTNER_EMAIL]: userPreference.data?.[PARTNER_EMAIL] || false,
      }),
  
      // ---------------- ACCOUNT ----------------
      ...(ownerAccountDtlDetails && {
        [ACCOUNT_ID]:
          ownerAccountDtlDetails.data?.[ACCOUNT_ID]?.toString() || "",
        [PAYMENT_TYPE]:
          ownerAccountDtlDetails.data?.[PAYMENT_TYPE]?.toString() || undefined,
        [ZELLE_URL]: ownerAccountDtlDetails.data?.[ZELLE_URL] || undefined,
        [ACH_ROUTING]: ownerAccountDtlDetails.data?.[ACH_ROUTING] || undefined,
        [ACH_ACCOUNT]: ownerAccountDtlDetails.data?.[ACH_ACCOUNT] || undefined,
        [ACH_ACCOUNT_NAME]:
          ownerAccountDtlDetails.data?.[ACH_ACCOUNT_NAME] || undefined,
        [ACH_BANK_NAME]:
          ownerAccountDtlDetails.data?.[ACH_BANK_NAME] || undefined,
        [E_ACCOUNT_NO]:
          ownerAccountDtlDetails.data?.[E_ACCOUNT_NO] || undefined,
        [E_ACCOUNT_NAME]:
          ownerAccountDtlDetails.data?.[E_ACCOUNT_NAME] || undefined,
        [E_ACCOUNT_EMAIL]:
          ownerAccountDtlDetails.data?.[E_ACCOUNT_EMAIL] || undefined,
        [E_ACCOUNT_PHONE]:
          ownerAccountDtlDetails.data?.[E_ACCOUNT_PHONE] || undefined,
        [E_COM_SIGNUP]:
          ownerAccountDtlDetails.data?.[E_COM_SIGNUP] || undefined,
        [E_COM_START_DATE]:
          ownerAccountDtlDetails.data?.[E_COM_START_DATE] || undefined,
        [AUTO_BILLING_ENROLL]:
          ownerAccountDtlDetails.data?.[AUTO_BILLING_ENROLL] || undefined,
        [AUTO_BILLING_START_DATE]:
          ownerAccountDtlDetails.data?.[AUTO_BILLING_START_DATE] || undefined,
        [AUTO_BILLING_LA]:
          ownerAccountDtlDetails.data?.[AUTO_BILLING_LA] || undefined,
        [AUTO_BILLING_LA_START_DATE]:
          ownerAccountDtlDetails.data?.[AUTO_BILLING_LA_START_DATE] || undefined,
        [AUTO_BILLING_LA_END_DATE]:
          ownerAccountDtlDetails.data?.[AUTO_BILLING_LA_END_DATE] || undefined,
      }),
    });
  
  }, [userDetails, userPreference, ownerAccountDtlDetails, stateList]);

  const downloadPDF = async () => {
    const element = document.getElementById("pdf-content");
    if (!element) return;
  
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });
  
    // document.body.removeChild(element);
  
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
  
    const pageWidth = 210;
    const imgHeight = (canvas.height * pageWidth) / canvas.width;
  
    pdf.addImage(imgData, "PNG", 0, 0, pageWidth, imgHeight);
    pdf.save("MyProfile.pdf");
    window.close();
  }

  useEffect(() => {
    if (!userDetails) return;

    if (isLoadingUserPreference || isOwnerAccountDtlDetailsLoading) return;

    const timer = setTimeout(() => {
      downloadPDF();
    }, 300); // 300ms is safe for heavy DOM

    return () => clearTimeout(timer);
    
  }, [userDetails, isLoadingUserPreference, isOwnerAccountDtlDetailsLoading]);

  return {
    isUserDetailsLoading,
    isLoadingUserPreference,
    isOwnerAccountDtlDetailsLoading,
    form
  }
};

export default useDownloadFile; 
